import * as React from 'react';
import { cva } from 'class-variance-authority';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Card, CardContent } from './card';

/**
 * StatCard - KPI/Metric card with Hyperkit styling
 *
 * Hyperkit Specs:
 * - Card: 8px radius, 24px padding
 * - Value: 36px font, -2% letter-spacing
 * - Trend: colored based on direction
 * - Icon: 36px container, semantic color backgrounds
 */

const iconContainerVariants = cva(
  'flex h-9 w-9 items-center justify-center rounded-lg shrink-0',
  {
    variants: {
      color: {
        default: 'bg-grey-100 text-grey-600 dark:bg-grey-800 dark:text-grey-400',
        success: 'bg-green-500/15 text-green-500',
        warning: 'bg-orange-500/15 text-orange-500',
        danger: 'bg-red-500/15 text-red-500',
        info: 'bg-blue-500/15 text-blue-500',
      },
    },
    defaultVariants: {
      color: 'default',
    },
  }
);

const trendVariants = cva(
  'flex items-center gap-1 text-body-sm font-medium',
  {
    variants: {
      trend: {
        up: 'text-green-500',
        down: 'text-red-500',
        neutral: 'text-grey-500 dark:text-grey-400',
      },
    },
    defaultVariants: {
      trend: 'neutral',
    },
  }
);

const StatCard = React.forwardRef(({
  className,
  label,
  value,
  change,
  changeLabel = 'vs yesterday',
  trend = 'neutral',
  icon: Icon,
  color,
  onClick,
  ...props
}, ref) => {
  // Determine trend from change value if not explicitly set
  const computedTrend = trend !== 'neutral' ? trend :
    (change > 0 ? 'up' : change < 0 ? 'down' : 'neutral');

  const TrendIcon = computedTrend === 'up' ? TrendingUp :
    computedTrend === 'down' ? TrendingDown : Minus;

  return (
    <Card
      ref={ref}
      className={cn(
        'transition-all duration-200',
        onClick && 'cursor-pointer hover:border-blue-500 hover:shadow-[0_0_0_3px_rgba(99,102,233,0.1)]',
        className
      )}
      onClick={onClick}
      {...props}
    >
      <CardContent className="p-5">
        {/* Header: Label + Icon */}
        <div className="flex items-start justify-between mb-3">
          <span className="text-body-sm text-grey-500 dark:text-grey-400 font-medium">
            {label}
          </span>
          {Icon && (
            <div className={iconContainerVariants({ color })}>
              <Icon size={18} />
            </div>
          )}
        </div>

        {/* Value */}
        <div className="text-display-xs text-grey-900 dark:text-grey-50 mb-2">
          {value}
        </div>

        {/* Trend */}
        {(change !== undefined || changeLabel) && (
          <div className={trendVariants({ trend: computedTrend })}>
            <TrendIcon size={14} />
            <span>
              {change !== undefined && (change > 0 ? `+${change}` : change)}
              {changeLabel && ` ${changeLabel}`}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
});
StatCard.displayName = 'StatCard';

/**
 * StatCardSkeleton - Loading state for StatCard
 */
const StatCardSkeleton = React.forwardRef(({ className, ...props }, ref) => (
  <Card ref={ref} className={cn('animate-pulse', className)} {...props}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="h-4 w-24 rounded bg-grey-200 dark:bg-grey-700" />
        <div className="h-9 w-9 rounded-lg bg-grey-200 dark:bg-grey-700" />
      </div>
      <div className="h-9 w-16 rounded bg-grey-200 dark:bg-grey-700 mb-2" />
      <div className="h-4 w-20 rounded bg-grey-200 dark:bg-grey-700" />
    </CardContent>
  </Card>
));
StatCardSkeleton.displayName = 'StatCardSkeleton';

export { StatCard, StatCardSkeleton, iconContainerVariants, trendVariants };
